package dk.smark.chat

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import dk.smark.chat.network.FriendApiService
import dk.smark.chat.network.FriendRequest
import dk.smark.chat.network.User
import dk.smark.chat.network.UserApiService
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch

class FriendsViewModel(
    private val friendApiService: FriendApiService,
    private val userApiService: UserApiService
) : ViewModel() {

    private val _friends = MutableStateFlow<List<User>>(emptyList())
    val friends: StateFlow<List<User>> = _friends

    private val _pendingRequests = MutableStateFlow<List<FriendRequest>>(emptyList())
    val pendingRequests: StateFlow<List<FriendRequest>> = _pendingRequests

    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading

    private val _error = MutableStateFlow<String?>(null)
    val error: StateFlow<String?> = _error

    fun load(userId: Int) {
        viewModelScope.launch {
            _isLoading.value = true
            _error.value = null
            try {
                _friends.value = friendApiService.getFriends(userId)
                _pendingRequests.value = friendApiService.getReceivedFriendRequests(userId)
            } catch (e: Exception) {
                _error.value = e.message ?: "Failed to load friends"
            } finally {
                _isLoading.value = false
            }
        }
    }

    /**
     * Looks up [email] in the user directory.
     * - If found → sends a friend request from [userId].
     * - If not found → sends an email invitation via the invite endpoint.
     */
    fun addOrInvite(
        userId: Int,
        inviterName: String,
        email: String,
        onSuccess: (String) -> Unit,
        onError: (String) -> Unit
    ) {
        viewModelScope.launch {
            try {
                val existingUser = try {
                    userApiService.getUserByEmail(email)
                } catch (_: Exception) {
                    null
                }

                if (existingUser != null) {
                    friendApiService.sendFriendRequest(userId, email)
                    onSuccess("Friend request sent to ${existingUser.name}!")
                } else {
                    friendApiService.inviteContact(email, inviterName)
                    onSuccess("Invitation email sent to $email!")
                }
            } catch (e: Exception) {
                onError(e.message ?: "Operation failed")
            }
        }
    }

    fun acceptRequest(requestId: Int, userId: Int) {
        viewModelScope.launch {
            try {
                friendApiService.acceptFriendRequest(requestId)
                load(userId)
            } catch (e: Exception) {
                _error.value = e.message ?: "Failed to accept request"
            }
        }
    }

    fun declineRequest(requestId: Int, userId: Int) {
        viewModelScope.launch {
            try {
                friendApiService.declineFriendRequest(requestId)
                _pendingRequests.value = _pendingRequests.value.filter { it.id != requestId }
            } catch (e: Exception) {
                _error.value = e.message ?: "Failed to decline request"
            }
        }
    }
}
